/// <reference path="./virtual-pwa.d.ts" />
import { type Plugin, effectScope } from 'vue';
import {
  SW_APPLY_UPDATE_KEY,
  SW_NEED_REFRESH_KEY,
  SW_UPDATE_SERVICE_WORKER_KEY,
  type ServiceWorkerEvent,
  type ServiceWorkerEventHandler,
} from './types/service-worker.types';
import { useRegisterSW } from 'virtual:pwa-register/vue';

const DEFAULT_UPDATE_CHECK_INTERVAL_MS = 2 * 60 * 60 * 1000;
const RELOAD_GUARD_KEY = 'sw-reload-attempt';
const RELOAD_GUARD_COUNT_KEY = 'sw-reload-count';
const RELOAD_GUARD_COOLDOWN_MS = 10_000;
const RELOAD_GUARD_RESET_MS = 5 * 60_000;
const MAX_RELOAD_RETRIES = 2;
const ACTIVATION_TIMEOUT_MS = 12_000;
const MANUAL_ACTIVATION_TIMEOUT_MS = 3_000;

interface RouterLike {
  onError: (fn: (e: Error) => void) => void;
  beforeEach: (
    guard: (
      to: { fullPath: string },
      from: { fullPath: string },
    ) => boolean | undefined,
  ) => () => void;
}

export interface ServiceWorkerPluginOptions {
  updateCheckInterval?: number;
  router?: RouterLike;
  onEvent?: ServiceWorkerEventHandler;
}

const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS/i;

interface PreloadErrorEvent extends Event {
  payload?: unknown;
}

const isChunkLoadError = (message: string): boolean =>
  CHUNK_LOAD_ERROR_PATTERN.test(message);

// Under hash routing, navigating to a URL that differs only by fragment is a
// same-document navigation and never reloads the document, so an explicit
// reload() is always required to boot the new bundle.
const pathOf = (location: { fullPath: string }): string =>
  location.fullPath.split(/[?#]/)[0];

const hardReloadTo = (path: string) => {
  const targetHash = `#${path}`;
  if (window.location.hash !== targetHash) {
    window.location.replace(`${import.meta.env.BASE_URL || '/'}${targetHash}`);
  }
  window.location.reload();
};

/**
 * Registers a service worker and, when a new version is detected, applies the
 * update on the next Vue Router navigation.
 *
 * The update flow is fail-open: navigation is never blocked. The user keeps
 * navigating on the current version (still fully served by the old service
 * worker precache) while the new worker activates; the page hard-reloads to
 * the latest visited route as soon as the new worker takes control. Any
 * failure in the flow (update rejection, activation timeout) disarms the
 * plugin for the session instead of leaving the app stuck — the update will
 * apply on the next natural full reload.
 */
export const ServiceWorkerPlugin: Plugin<ServiceWorkerPluginOptions> = (
  app,
  options,
) => {
  const {
    updateCheckInterval = DEFAULT_UPDATE_CHECK_INTERVAL_MS,
    router,
    onEvent,
  } = options ?? {};

  const emit = (event: ServiceWorkerEvent) => {
    if (!onEvent) return;
    try {
      onEvent(event);
    } catch {
      // Telemetry must never break registration or navigation.
    }
  };

  let registration: ServiceWorkerRegistration | undefined;

  const scope = effectScope();

  const result = scope.run(() =>
    useRegisterSW({
      immediate: true,
      onNeedRefresh() {
        emit({ type: 'update-available' });
      },
      onRegisterError(error: unknown) {
        emit({ type: 'register-error', message: toErrorMessage(error) });
      },
      onRegisteredSW(
        _: string,
        swRegistration: ServiceWorkerRegistration | undefined,
      ) {
        registration = swRegistration;
        if (swRegistration) {
          const scheduleUpdate = () => {
            setTimeout(() => {
              // Skip when tab is hidden or an update is already pending.
              if (
                document.visibilityState === 'hidden' ||
                result?.needRefresh.value
              ) {
                scheduleUpdate();
                return;
              }

              swRegistration
                .update()
                .catch((error: unknown) => {
                  emit({
                    type: 'update-check-failed',
                    message: toErrorMessage(error),
                  });
                })
                .finally(scheduleUpdate);
            }, updateCheckInterval);
          };
          scheduleUpdate();
        }
      },
    }),
  );

  if (!result) {
    throw new Error(
      'ServiceWorkerPlugin: effectScope.run() returned undefined.',
    );
  }

  const { needRefresh, updateServiceWorker } = result;

  const recoverFromChunkLoad = (message: string) => {
    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    const storedReloadCount = Number(
      sessionStorage.getItem(RELOAD_GUARD_COUNT_KEY),
    );
    const now = Date.now();
    const isRecentReload =
      Number.isFinite(lastReload) && now - lastReload <= RELOAD_GUARD_RESET_MS;
    const reloadCount =
      isRecentReload && Number.isFinite(storedReloadCount)
        ? storedReloadCount
        : 0;

    emit({ type: 'chunk-error', message, data: { reloadCount } });

    if (
      Number.isFinite(lastReload) &&
      now - lastReload <= RELOAD_GUARD_COOLDOWN_MS
    ) {
      emit({ type: 'reload-guard-cooldown', data: { reloadCount } });
      return;
    }
    if (reloadCount >= MAX_RELOAD_RETRIES) {
      emit({ type: 'reload-guard-exhausted', data: { reloadCount } });
      return;
    }

    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    sessionStorage.setItem(RELOAD_GUARD_COUNT_KEY, String(reloadCount + 1));
    window.location.reload();
  };

  router?.onError((error) => {
    if (isChunkLoadError(error.message)) {
      recoverFromChunkLoad(error.message);
    }
  });

  // CSS preload failures surface as a window `vite:preloadError` event rather
  // than a router error; preventDefault stops Vite from rethrowing so the same
  // guarded reload can recover the stale bundle.
  if (
    typeof window !== 'undefined' &&
    typeof window.addEventListener === 'function'
  ) {
    window.addEventListener('vite:preloadError', (event) => {
      event.preventDefault();
      recoverFromChunkLoad(
        toErrorMessage((event as PreloadErrorEvent).payload),
      );
    });
  }

  let updateState: 'idle' | 'updating' | 'disarmed' = 'idle';
  let targetPath = '';

  const requestActivation = (config: {
    timeoutMs: number;
    onActivated: (waitedMs: number) => void;
    onTimeout: () => void;
    onRejected: (error: unknown) => void;
  }) => {
    const startedAt = Date.now();

    const onControllerChange = () => {
      clearTimeout(timeout);
      config.onActivated(Date.now() - startedAt);
    };

    const timeout = setTimeout(() => {
      navigator.serviceWorker?.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
      config.onTimeout();
    }, config.timeoutMs);

    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      onControllerChange,
      { once: true },
    );

    updateServiceWorker(false).catch((error: unknown) => {
      clearTimeout(timeout);
      navigator.serviceWorker?.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
      config.onRejected(error);
    });
  };

  const applyUpdate = () => {
    if (!needRefresh.value) return;

    emit({ type: 'manual-update-requested' });

    if (updateState === 'updating') return;

    if (registration && !registration.waiting) {
      emit({ type: 'waiting-missing', data: { trigger: 'manual' } });
      window.location.reload();
      return;
    }

    updateState = 'updating';

    // The user explicitly asked for the update, so every outcome ends in a
    // reload — worst case the old version loads again and the icon stays.
    requestActivation({
      timeoutMs: MANUAL_ACTIVATION_TIMEOUT_MS,
      onActivated: (waitedMs) => {
        emit({
          type: 'controller-change-reload',
          data: { waitedMs, trigger: 'manual' },
        });
        window.location.reload();
      },
      onTimeout: () => {
        emit({
          type: 'activation-timeout',
          data: { waitedMs: MANUAL_ACTIVATION_TIMEOUT_MS, trigger: 'manual' },
        });
        window.location.reload();
      },
      onRejected: (error) => {
        emit({
          type: 'update-rejected',
          message: toErrorMessage(error),
          data: { trigger: 'manual' },
        });
        window.location.reload();
      },
    });
  };

  if (router) {
    router.beforeEach((to, from) => {
      if (updateState === 'updating') {
        targetPath = to.fullPath;
        return;
      }

      if (!needRefresh.value || updateState === 'disarmed') return;
      if (pathOf(to) === pathOf(from)) return;

      // A pending update with no waiting worker means another tab already
      // activated the new service worker: this page runs stale code against
      // the new precache, so reload to the target route right away.
      if (registration && !registration.waiting) {
        emit({ type: 'waiting-missing', data: { route: to.fullPath } });
        hardReloadTo(to.fullPath);
        return false;
      }

      updateState = 'updating';
      targetPath = to.fullPath;
      emit({ type: 'update-flow-entered', data: { route: to.fullPath } });

      requestActivation({
        timeoutMs: ACTIVATION_TIMEOUT_MS,
        onActivated: (waitedMs) => {
          emit({
            type: 'controller-change-reload',
            data: { waitedMs, route: targetPath },
          });
          hardReloadTo(targetPath);
        },
        onTimeout: () => {
          updateState = 'disarmed';
          emit({
            type: 'activation-timeout',
            data: { waitedMs: ACTIVATION_TIMEOUT_MS },
          });
        },
        onRejected: (error) => {
          updateState = 'disarmed';
          emit({ type: 'update-rejected', message: toErrorMessage(error) });
        },
      });

      return;
    });
  }

  app.provide(SW_NEED_REFRESH_KEY, needRefresh);
  app.provide(SW_UPDATE_SERVICE_WORKER_KEY, updateServiceWorker);
  app.provide(SW_APPLY_UPDATE_KEY, applyUpdate);
};
